import { FC, MouseEvent, PropsWithChildren, useState } from "react";
import styled, { css } from "styled-components";

import { FormLayout, Larger } from "@/components/Forms";
import { SrOnly } from "@/components/Layout/Parts";
import Text from "@/components/Text";

const Card = styled.div<{ $hasErrors: boolean }>`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  ${({ $hasErrors }) =>
    $hasErrors &&
    css`
      border-color: ${({ theme }) => theme.colors.error};
    `}
`;

const GearIcon = styled.div`
  color: ${({ theme }) => theme.colors.black};
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  flex: 1;
  padding: 0 30px 0 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Button = styled.button`
  position: relative;
  transition: 0.2s ease-out border-color;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.black};
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }
`;

const DropSvg = styled.svg<{ $isOpen: boolean }>`
  transition: 0.2s ease-out transform;
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

const Border = styled(Larger)`
  height: 1px;
  background: ${({ theme }) => theme.colors.grayscale};
  margin: 8px 0;
`;

interface Props {
  title: string;
  subtitle?: string;
  withoutBorder?: boolean;
  className?: string;
  hasErrors?: boolean;
}

const TestingPropertiesFields: FC<PropsWithChildren<Props>> = ({
  children,
  title,
  withoutBorder = false,
  subtitle = "Keep default or set different values",
  className,
  hasErrors = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  return (
    <FormLayout className={className}>
      <Larger>
        <Card $hasErrors={hasErrors}>
          <GearIcon>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 12.0001C10.2091 12.0001 12 10.2092 12 8.00006C12 5.79092 10.2091 4.00006 8 4.00006C5.79086 4.00006 4 5.79092 4 8.00006C4 10.2092 5.79086 12.0001 8 12.0001Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.6448 7.08076C14.59 7.16289 14.5608 7.2594 14.5608 7.35811V8.64189C14.5608 8.74061 14.59 8.83711 14.6448 8.91925L15.4718 10.1597L15.4717 10.1597L15.4732 10.1619C15.4861 10.181 15.4946 10.2026 15.4981 10.2254C15.5016 10.2475 15.5002 10.2701 15.4942 10.2916C15.3481 10.7718 15.1559 11.2368 14.9203 11.6799C14.9095 11.699 14.8948 11.7156 14.877 11.7285C14.8588 11.7418 14.8378 11.7509 14.8156 11.7552L14.8156 11.7552L14.8119 11.7559L13.3485 12.0485C13.2517 12.0679 13.1628 12.1155 13.093 12.1853L12.1853 13.0931C12.1155 13.1629 12.0679 13.2518 12.0485 13.3486L11.7558 14.812L11.7558 14.812L11.7551 14.8157C11.7508 14.8379 11.7417 14.8589 11.7284 14.8771C11.7155 14.8948 11.699 14.9096 11.6799 14.9204C11.2367 15.156 10.7717 15.3482 10.2915 15.4942C10.27 15.5002 10.2474 15.5016 10.2253 15.4981C10.2025 15.4946 10.1809 15.4861 10.1618 15.4732L10.1596 15.4718L8.91917 14.6448C8.83703 14.59 8.74053 14.5608 8.64181 14.5608H7.35803C7.25933 14.5608 7.16283 14.59 7.08069 14.6448L5.8402 15.4717L5.83801 15.4732C5.81894 15.486 5.79729 15.4945 5.77458 15.4981L5.85056 15.9854L5.77457 15.4981C5.75246 15.5015 5.72988 15.5002 5.70833 15.4942C5.22811 15.3481 4.76316 15.156 4.31998 14.9203C4.3009 14.9095 4.28434 14.8948 4.27143 14.8771C4.25812 14.8588 4.24901 14.8378 4.24474 14.8156L4.24476 14.8156L4.24402 14.8119L3.95139 13.3485C3.93204 13.2517 3.88446 13.1628 3.81465 13.093L2.9069 12.1853C2.8371 12.1155 2.74821 12.0679 2.65141 12.0485L1.188 11.7558L1.188 11.7558L1.18432 11.7551C1.16213 11.7509 1.14115 11.7417 1.12288 11.7284C1.10516 11.7155 1.09042 11.699 1.07964 11.6799C0.844002 11.2367 0.65182 10.7717 0.505781 10.2915C0.499763 10.2699 0.498428 10.2473 0.501877 10.2252C0.505419 10.2025 0.513918 10.1808 0.526778 10.1618L0.528236 10.1596L1.35517 8.91916C1.40993 8.83703 1.43915 8.74052 1.43915 8.64181V7.35803C1.43915 7.25932 1.40993 7.16282 1.35517 7.08068L0.52824 5.84028L0.528246 5.84028L0.526766 5.83808C0.513912 5.81902 0.505412 5.79736 0.50187 5.77465C0.498422 5.75253 0.499754 5.72994 0.50577 5.7084C0.651854 5.22815 0.844073 4.76317 1.07974 4.31995C1.09051 4.30088 1.10525 4.28432 1.12298 4.27141C1.14123 4.2581 1.16221 4.24899 1.18439 4.24473L1.1844 4.24474L1.18808 4.24401L2.65139 3.95139C2.74819 3.93203 2.8371 3.88445 2.9069 3.81465L3.81473 2.90682C3.88454 2.83702 3.93211 2.74811 3.95147 2.65131L4.24409 1.18801L4.24411 1.18801L4.24482 1.18432C4.24908 1.16213 4.25819 1.14116 4.2715 1.12289C4.28441 1.10517 4.30097 1.09043 4.32005 1.07965C4.76327 0.844026 5.22824 0.651838 5.70848 0.505774C5.73003 0.499756 5.75262 0.498421 5.77474 0.50187C5.79746 0.505411 5.81912 0.513911 5.83818 0.526765L5.83817 0.526771L5.84036 0.52823L7.08076 1.35516C7.1629 1.40992 7.2594 1.43914 7.35811 1.43914H8.64189C8.7406 1.43914 8.83711 1.40992 8.91924 1.35516L10.1596 0.528236L10.1618 0.526765C10.1809 0.51391 10.2025 0.505411 10.2253 0.501869C10.2474 0.498421 10.27 0.499756 10.2915 0.505776C10.7718 0.65184 11.2367 0.84403 11.68 1.07966C11.699 1.09043 11.7156 1.10518 11.7285 1.1229C11.7418 1.14116 11.7509 1.16213 11.7552 1.18432L11.7552 1.18432L11.7559 1.18801L12.0486 2.65141C12.068 2.7482 12.1156 2.8371 12.1853 2.9069L13.0931 3.81465C13.1629 3.88445 13.2518 3.93202 13.3486 3.95138L14.8119 4.24408L14.8119 4.24409L14.8156 4.2448C14.8378 4.24906 14.8588 4.25818 14.877 4.27148C14.8947 4.2844 14.9095 4.30095 14.9203 4.32003C15.1559 4.76321 15.3481 5.22816 15.4942 5.70837C15.5002 5.72992 15.5016 5.75252 15.4981 5.77465C15.4946 5.79737 15.4861 5.81903 15.4732 5.83808L15.4718 5.84028L14.6448 7.08076Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </GearIcon>
          <Content>
            <Text $type="smallBold">{title}</Text>
            <Text $type="smallMedium" $color="gray">
              {subtitle}
            </Text>
          </Content>
          <Button onClick={onToggle}>
            <SrOnly>Toggle fields</SrOnly>
            <DropSvg
              $isOpen={isOpen}
              width="8"
              height="6"
              viewBox="0 0 8 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="currentColor" />
            </DropSvg>
          </Button>
        </Card>
      </Larger>
      {isOpen && children}
      {!withoutBorder && <Border />}
    </FormLayout>
  );
};

export default TestingPropertiesFields;
